/* @flow */
import cx from 'classnames';
import * as React from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';

import { pushRouterToChatWithMember } from 'lib/ChatRouteHelper';
import Button from 'components/ButtonDeprecated';
import ChatActions from 'chat/actions/ChatActions';
import ChatMembers from 'collections/ChatMembers';
import ChatStore from 'chat/stores/ChatStore';
import CurrentUser from 'auth/CurrentUser';
import DeprecatedTokenizerTypeahead from 'components/DeprecatedTokenizerTypeahead';
import getMatcher from 'utils/getMatcher';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageStarterItem from './MessageStarterItem';
import PotentialChatMember from 'entities/PotentialChatMember';
import PotentialChatMembers from 'collections/PotentialChatMembers';
import RecipientToken from 'chat/components/ComposerRecipientToken';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import UserAvatar from 'users/components/UserAvatar';

import './styles/GroupMessageStarter.scss';

type Props = {
  groupCode?: string,
  additionalChatMembersLimit: number,
  onRequestHide?: Function,
  className?: ?string,
  createRemoteChat?: boolean,
  onCreateRemoteChat?: Function,
};

type State = {
  selectedMembers: PotentialChatMember[],
};

class GroupMessageStarter extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state: State = {
    selectedMembers: [],
  };

  componentDidMount() {
    this.fetchData('');
  }

  getFilter(query: string) {
    const matcher = getMatcher(query);
    return (member: PotentialChatMember) => matcher([member.getQueryKey()]);
  }

  fetchData(query: string): Promise<PotentialChatMember[]> {
    return ChatActions.promisePotentialChatMembers(query)
      .then((coll: PotentialChatMembers) => coll.filter(m => m.isEligible()))
      .then((members: PotentialChatMember[]) => sortBy(members, ['sort_key']));
  }

  handleSelectionChange = (selectedMembers: PotentialChatMember[]) => {
    this.setState({ selectedMembers });
  };

  createLocalChat = (originIds: Backbone$Id[]) => {
    const currentMember = ChatMembers.buildChatMember(CurrentUser.getUser());
    const members = [currentMember].concat(this.state.selectedMembers);
    const title = '';

    ChatActions.loadLocal(members, title, originIds);
    ChatActions.resetSelection(); // deselect current chat to focus on local
  };

  startConversation = () => {
    const { selectedMembers } = this.state;

    // if we are starting a 1 to 1 chat:
    if (selectedMembers.length === 1) {
      pushRouterToChatWithMember(this.context.router, selectedMembers[0], null);
      if (this.props.onRequestHide) {
        this.props.onRequestHide();
      }
      return;
    }

    // Send-to-few requires the chat UUID so we must create the chat before the message is sent.
    if (this.props.createRemoteChat) {
      this.createLocalChat([]);
      ChatActions.createChat(ChatStore.getLocal(), null)
        .then(chat => {
          ChatActions.destroyLocal();
          if (this.props.onCreateRemoteChat) {
            this.props.onCreateRemoteChat(chat);
          }
          if (this.props.onRequestHide) {
            this.props.onRequestHide();
          }
        })
        .catch(error => {
          ToastActions.showErrorToast(error.error);
          if (this.props.onRequestHide) {
            this.props.onRequestHide();
          }
        });
      return;
    }

    // if we are starting a group chat:
    // Timeout to prevent multiple dispatches at the same time
    setTimeout(() => {
      const conversationPath = `/classes/r/messages/new`;
      this.createLocalChat([]);
      this.context.router.replace(conversationPath);
    }, 0);
  };

  isAtChatMembersLimit = () => {
    const { selectedMembers } = this.state;
    return selectedMembers.length === this.props.additionalChatMembersLimit;
  };

  isOverChatMembersLimit = () => {
    const { selectedMembers } = this.state;
    return selectedMembers.length > this.props.additionalChatMembersLimit;
  };

  renderActionButton = () => {
    return (
      <Button disabled={this.state.selectedMembers.length === 0} onClick={this.startConversation}>
        Go
      </Button>
    );
  };

  renderInputToken(member: PotentialChatMember) {
    const user = member.getUser();
    return (
      <RecipientToken>
        <UserAvatar user={user.toJSON()} size="xsmall" />
        {member.getName()}
      </RecipientToken>
    );
  }

  renderParticipantRemainingCount = () => {
    const { selectedMembers } = this.state;
    if (selectedMembers.length === 0) {
      return null;
    }

    const { additionalChatMembersLimit } = this.props;
    const remainingCount = additionalChatMembersLimit - selectedMembers.length;
    let text = `You can add ${remainingCount} more people`;
    if (this.isOverChatMembersLimit()) {
      text = 'You have exceeded the maximum number of participants';
    } else if (this.isAtChatMembersLimit()) {
      text = 'You have reached the maximum number of participants';
    } else if (remainingCount === 1) {
      text = 'You can add 1 more person';
    }

    return (
      <Text color={this.isOverChatMembersLimit() ? 'apple' : 'percussion'} size="xxsmall">
        {text}
      </Text>
    );
  };

  renderMember = (
    member: PotentialChatMember,
    highlighted: boolean,
    query: string,
    onSelect: Function
  ) => {
    return (
      <li onClick={() => onSelect()} key={member.id}>
        <MessageStarterItem
          className={cx('GroupMessageStarter-results-item', {
            'GroupMessageStarter-results-item--highlight': highlighted,
          })}
          member={member}
          query={query}
          selectable={this.state.selectedMembers.length > 0}
          selected={this.state.selectedMembers.includes(member)}
        />
      </li>
    );
  };

  renderList = (items: Array<*>, loading: boolean) => {
    return (
      <div>
        {this.renderParticipantRemainingCount()}
        <div className="GroupMessageStarter-resultsWrap">
          <ul className="GroupMessageStarter-results">{items}</ul>
          {loading && (
            <div className="GroupMessageStarter-loadingIndicator">
              <LoadingIndicator />
            </div>
          )}
        </div>
      </div>
    );
  };

  renderNoResults(query: string) {
    return (
      <li key="noResults">
        <Text color="chalkboard" size="xsmall" weight="semibold">
          No people found for {`"${query}"`}
        </Text>
      </li>
    );
  }

  render() {
    const actionButton = (
      <Button
        data-test-selector="start_group_chat_button"
        disabled={this.state.selectedMembers.length === 0 || this.isOverChatMembersLimit()}
        onClick={this.startConversation}>
        Go
      </Button>
    );

    return (
      <div className={cx('GroupMessageStarter', this.props.className)}>
        <DeprecatedTokenizerTypeahead
          prompt="To:"
          placeholder="Select or search for people"
          // $FlowFixMe
          value={this.state.selectedMembers || []}
          onChange={this.handleSelectionChange}
          dataSource={this.fetchData}
          dataFilterFactory={this.getFilter}
          listRenderer={this.renderList}
          itemRenderer={this.renderMember}
          tokenRenderer={this.renderInputToken}
          noResultsRenderer={this.renderNoResults}
          actionButton={actionButton}
          autoFocus
          inline
        />
      </div>
    );
  }
}

export { GroupMessageStarter };

export default GroupMessageStarter;
